import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSourceChannelsFilters,
  selectSourceChannelsMeta,
  selectSourceChannelsPagination,
  selectSourceChannelsSort,
  setSourceSearch,
  setSourceStatusFilter,
} from "../../store/slices/sourceChannelsSlice";
import { selectAccessToken } from "../../store/slices/authSlice";
import { forceLogoutToLogin, isUnauthorizedApiResponse } from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const statusChips = [
  { label: "All", value: "ALL" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const SourceChannelFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectSourceChannelsFilters);
  const pagination = useSelector(selectSourceChannelsPagination);
  const meta = useSelector(selectSourceChannelsMeta);
  const sort = useSelector(selectSourceChannelsSort);
  const accessToken = useSelector(selectAccessToken);
  const [searchValue, setSearchValue] = useState(filters.search);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const openMenu = Boolean(menuAnchor);

  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setSourceSearch(searchValue.trim()));
    }, 350);
    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", pagination.page + 1);
    params.append("pageSize", pagination.pageSize);
    if (filters.search.trim()) {
      params.append("search", filters.search.trim());
    }
    if (filters.status !== "ALL") {
      params.append("status", filters.status);
    }
    if (sort?.field) {
      params.append("sortBy", sort.field);
      params.append("sortDirection", sort.direction || "asc");
    }
    return params.toString();
  }, [filters, pagination, sort]);

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/source-channels/export?${queryString}`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );

      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (isUnauthorizedApiResponse(response, payload)) {
          forceLogoutToLogin();
          return;
        }
        throw new Error(
          payload?.resultDesc || response.statusText || "Failed to download file."
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `source-channels-${new Date().toISOString()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error.message || "Failed to download file.");
    } finally {
      setExporting(false);
      setMenuAnchor(null);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          overflowX: "auto",
          py: 0.5,
          px: 0.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {statusChips.map((chip) => {
          const isActive = filters.status === chip.value;
          const countValue = meta?.statusCounts?.[chip.value] ?? 0;
          return (
            <Chip
              key={chip.value}
              label={
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography component="span" fontWeight={600}>
                    {chip.label}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      height: 20,
                      borderRadius: 999,
                      backgroundColor: isActive
                        ? "rgba(255,255,255,0.24)"
                        : "rgba(15, 23, 42, 0.06)",
                      color: isActive ? "#ffffff" : "text.secondary",
                      px: 1,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {countValue}
                  </Box>
                </Box>
              }
              color={isActive ? "primary" : "default"}
              variant={isActive ? "filled" : "outlined"}
              onClick={() => dispatch(setSourceStatusFilter(chip.value))}
              sx={{
                borderRadius: 10,
                height: 38,
                fontWeight: 600,
                color: isActive ? "primary.contrastText" : "text.secondary",
              }}
            />
          );
        })}
      </Stack>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ lg: "center" }}
        justifyContent="space-between"
      >
        <TextField
          fullWidth
          placeholder="Search by name"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            width: 48,
            height: 48,
            flexShrink: 0,
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={openMenu}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            elevation: 6,
            sx: {
              boxShadow: "0px 22px 45px rgba(15, 23, 42, 0.18)",
              border: "1px solid rgba(148, 163, 184, 0.16)",
              background:
                "radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff",
              borderRadius: 3,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={handleExport} disabled={exporting}>
            <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
            {exporting ? "Preparing..." : "Download Excel"}
          </MenuItem>
        </Menu>
      </Stack>
      {exportError && (
        <Typography variant="body2" color="error" sx={{ mt: -1 }}>
          {exportError}
        </Typography>
      )}
    </Stack>
  );
};

export default SourceChannelFilters;
